import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  capitalize,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { QuickSendContext } from "contexts/QuickSend";
import { useContext, useEffect, useState } from "react";
import { Link, Route, Routes, useNavigate } from "react-router-dom";
import { ALL } from "schemas/forms";
import { SetAlertType } from "schemas/functions";
import { routes } from "schemas/routes";
import { LIGHT_GREY } from "styles/oneOffColor";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import { StyledTableCell } from "components/Styled/Table/sharedStyles";
import { PAGE_VISITED } from "constants/trackingProps";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { titleCase } from "utils/string";
import { formatEpochTimestamp } from "utils/time";
import { trackEvent } from "utils/tracking";
import { removeFromList } from "utils/updateLocalState";

import EditTemplateDialog from "./EditTemplate";
import FilterMenu from "./FilterMenu";
import PromoAnnualBanner from "./PromoAnnualBanner";
import styles from "./styles";

interface TemplatesTableProps {
  setAlert: SetAlertType;
}
export default function TemplatesTable({ setAlert }: TemplatesTableProps) {
  const { getToken } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const { discoverTab } = useContext(DiscoverViewContext);

  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobileScreen =
    isMediumScreen || (isLargeScreen && discoverTab !== null);

  const { setErrorAlert } = useContext(AlertContext);

  const { currentOrg } = useContext(OrganizationUserContext);
  const {
    fetchOutreachTemplates,
    templates,
    setTemplates,
    fetchOutreachTemplatesLoading,
    outreachTemplates,
    setCreateTemplateDialogOpen,
  } = useContext(OutreachTemplatesContext);
  const { setNewlyAddedTemplateIndex } = useContext(QuickSendContext);
  const { setCreateTemplateOnboardingDialogOpen } = useContext(
    OnboardingDialogsContext,
  );

  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(
    null,
  );
  const [deleteTemplateLoading, setDeleteTemplateLoading] = useState(false);
  const [filter, setFilter] = useState(ALL);

  useEffect(() => {
    fetchOutreachTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    trackEvent(PAGE_VISITED);
  }, []);

  const noTemplatesFound =
    outreachTemplates?.length === 0 && !fetchOutreachTemplatesLoading;
  useEffect(() => {
    if (noTemplatesFound) {
      trackCreateTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noTemplatesFound]);

  const getFilteredTemplates = () => {
    let templatesWithIds = templates?.filter((x) => x.id);
    if (
      filter === TemplateContentType.OUTREACH ||
      filter === TemplateContentType.FOLLOWUP
    ) {
      templatesWithIds = templatesWithIds?.filter(
        (x) => x.contentType === filter,
      );
    }
    return templatesWithIds;
  };

  const handleOpenDeleteTemplate = (template: Template) => {
    trackEvent("Delete Template Button Clicked");
    setTemplateToDelete(template);
  };
  const handleDeleteTemplate = async () => {
    if (
      outreachTemplates?.length <= 1 &&
      templateToDelete?.contentType === TemplateContentType.OUTREACH
    ) {
      setAlert("Need at least one outreach template", "warning");
      return;
    }
    setDeleteTemplateLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/${templateToDelete?.id}`,
        "DELETE",
      );
      removeFromList(templates, setTemplates, templateToDelete, "id");
      handleClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setDeleteTemplateLoading(false);
    }
  };

  const handleClose = () => {
    setTemplateToDelete(null);
  };

  const trackCreateTemplate = () => {
    trackEvent("Add Template Button Clicked", {
      "Clicked From": "Templates Table View",
    });

    if (outreachTemplates?.length === 0) {
      setCreateTemplateOnboardingDialogOpen(true);
      return;
    }
    setCreateTemplateDialogOpen(true);
    setNewlyAddedTemplateIndex(-1);
  };

  const handleCopyTemplate = async (template: Template) => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates`,
        "POST",
        {},
        {
          name: `${template?.name} - Copy`,
          subject: template?.subject,
          body: template?.body,
          contentType: template?.contentType,
          attachment: template?.attachment,
        },
      );
      setTemplates((prev) => [...prev, res.template]);
      navigate(`/${routes.templates}/${res.template.id}`);
      setAlert("Successfully duplicated template", "success");
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const onTemplateSuccess = () => {
    navigate(`/${routes.templates}`, { replace: true });
  };

  return (
    <Box sx={styles.root}>
      <PromoAnnualBanner />

      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 3 }}
      >
        <Typography variant="h4">Your Templates</Typography>
        <Grid
          item
          xs={12}
          md="auto"
          container
          gap={1}
          justifyContent={isMobileScreen ? "flex-start" : "flex-end"}
        >
          <Button sx={{ textTransform: "none" }} onClick={trackCreateTemplate}>
            + Create Template
          </Button>
          <FilterMenu filter={filter} setFilter={setFilter} />
        </Grid>
      </Grid>

      {fetchOutreachTemplatesLoading ? (
        <CircularProgress />
      ) : (
        <TableContainer sx={styles.tableContainer}>
          <Table
            sx={[
              {
                maxWidth: { xs: "100%", md: isMobileScreen ? "100%" : "auto" },
                minWidth: { xs: "100%", md: isMobileScreen ? "100%" : 650 },
              },
              { backgroundColor: "white" },
            ]}
          >
            <TableHead>
              <TableRow>
                <StyledTableCell>Template Name</StyledTableCell>
                {!isMobileScreen && (
                  <StyledTableCell align="right">Last Modified</StyledTableCell>
                )}
                {!isMobileScreen && (
                  <StyledTableCell align="right">Template Type</StyledTableCell>
                )}
                <StyledTableCell align="right">Edit</StyledTableCell>
                <StyledTableCell align="right">Copy</StyledTableCell>
                <StyledTableCell align="right">Delete</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getFilteredTemplates().map(
                (template: Template, index: number) => (
                  <TableRow
                    key={index}
                    sx={styles.row}
                    onClick={() => {
                      navigate(`/${routes.templates}/${template.id}`);
                    }}
                  >
                    <TableCell component="th" scope="row">
                      <Grid container gap={1}>
                        <Typography sx={{ fontSize: 14, color: "black" }}>
                          {template?.name}{" "}
                        </Typography>
                        {isMobileScreen && (
                          <Chip
                            sx={{
                              fontSize: 10,
                              backgroundColor:
                                template?.contentType ===
                                TemplateContentType.OUTREACH
                                  ? "primary.main"
                                  : LIGHT_GREY,
                              color:
                                template?.contentType ===
                                TemplateContentType.OUTREACH
                                  ? "white"
                                  : "black",
                            }}
                            size="small"
                            label={titleCase(template?.contentType)}
                          ></Chip>
                        )}
                      </Grid>
                    </TableCell>

                    {!isMobileScreen && (
                      <TableCell align="right" sx={{ fontSize: 14 }}>
                        {template?.updatedAt
                          ? formatEpochTimestamp({
                              epochTimestamp: template?.updatedAt,
                            })
                          : "--"}
                      </TableCell>
                    )}

                    {!isMobileScreen && (
                      <TableCell align="right" size="small">
                        <Chip
                          label={capitalize(template?.contentType)}
                          sx={styles.contentTypeChip(template?.contentType)}
                        />
                      </TableCell>
                    )}

                    <TableCell align="right">
                      <IconButton
                        component={Link}
                        to={`/${routes.templates}/${template.id}`}
                        sx={styles.button}
                        onClick={() => {
                          trackEvent("Edit Template Button Clicked");
                        }}
                      >
                        <Box
                          component="i"
                          className="fa-regular fa-pen-to-square"
                        />
                      </IconButton>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        sx={styles.button}
                        onClick={(e) => {
                          e.stopPropagation();
                          trackEvent("Template Duplicated", {
                            "Template ID": template.id,
                          });
                          handleCopyTemplate(template);
                        }}
                      >
                        <Box
                          component="i"
                          className="fa-regular fa-clipboard"
                        />
                      </IconButton>
                    </TableCell>
                    <TableCell align="right">
                      <Box>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDeleteTemplate(template);
                          }}
                          sx={styles.button}
                        >
                          <Box
                            component="i"
                            className="fa-regular fa-trash-can"
                          />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Routes>
        <Route
          path=":templateId"
          element={<EditTemplateDialog onSuccess={onTemplateSuccess} />}
        />
      </Routes>

      <ConfirmDialog
        header={`Delete Template`}
        subheader={`Are you sure you want to delete ${templateToDelete?.name}?`}
        open={templateToDelete !== null}
        handleClose={handleClose}
        handleConfirm={handleDeleteTemplate}
        loading={deleteTemplateLoading}
      />
    </Box>
  );
}
